/**
 * Cache in-memory TTL per i lookup statici (categorie, fornitori, clienti,
 * spese-master). Riduce drasticamente le API duplicate durante la navigazione
 * tra le pagine principali del gestionale: prima di questa cache, ogni volta
 * che l'utente cambiava pagina scattavano 3-5 chiamate GET per popolare i
 * dizionari di lookup.
 *
 * Regole:
 *  - Chiave = URL + params serializzati (stessi params = stessa risposta).
 *  - TTL = 60s (i lookup cambiano di rado; una manciata di secondi di
 *    inconsistenza è accettabile — comunque la mutation invalida subito).
 *  - `bust(prefix)` permette di svuotare tutte le chiavi che iniziano con un
 *    prefisso (es. "/vendors" dopo un create/update/delete vendor).
 */

const CACHE = new Map(); // key -> { value, expiresAt }
const INFLIGHT = new Map(); // key -> Promise (deduplica chiamate concorrenti)

const DEFAULT_TTL_MS = 60_000;

const keyFor = (url, params) => `${url}?${JSON.stringify(params || {})}`;

/**
 * Esegue il fetcher SOLO se non è presente in cache (o è scaduto).
 * Deduplica anche le chiamate concorrenti sulla stessa chiave.
 */
export const cachedGet = async (url, params, fetcher, ttl = DEFAULT_TTL_MS) => {
  const key = keyFor(url, params);
  const now = Date.now();
  const hit = CACHE.get(key);
  if (hit && hit.expiresAt > now) return hit.value;

  const pending = INFLIGHT.get(key);
  if (pending) return pending;

  const p = (async () => {
    try {
      const value = await fetcher();
      CACHE.set(key, { value, expiresAt: Date.now() + ttl });
      return value;
    } finally {
      INFLIGHT.delete(key);
    }
  })();
  INFLIGHT.set(key, p);
  return p;
};

/**
 * Invalida tutte le chiavi che iniziano con `prefix` (URL). Usare dopo
 * create/update/delete che modificano il set restituito dal lookup.
 */
export const bust = (prefix) => {
  const keys = Array.from(CACHE.keys());
  for (const k of keys) {
    if (k.startsWith(prefix + "?") || k.startsWith(prefix + "/")) {
      CACHE.delete(k);
    }
  }
};

/** Utile per test/debug o per un logout forzato futuro. */
export const clearAll = () => {
  CACHE.clear();
  INFLIGHT.clear();
};
